import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { customer_login, messageClear } from '../store/reducers/authReducer';
import { Link } from 'react-router-dom';
import Footer from '../components/Footer'
import { toast } from 'react-hot-toast';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Eye, EyeOff, LoaderIcon } from "lucide-react";

const Login = () => {
  const { loader, successMessage, errorMessage, userInfo } = useSelector(state => state.auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [state, setState] = useState({
    email: '',
    password: ''
  });

  const [showPassword, setShowPassword] = useState(false);

  const inputHandle = (e) => {
    setState({
      ...state,
      [e.target.name]: e.target.value
    });
  };

  const login = (e) => {
    e.preventDefault();
    if (!state.email || !state.password) {
      toast.error("Email and password are required!");
      return;
    }
    dispatch(customer_login(state));
  };

  useEffect(() => {
    if (successMessage) {
      toast.success(successMessage);
      dispatch(messageClear());
    }
    if (errorMessage) {
      toast.error(errorMessage);
      dispatch(messageClear());
    }
    if (userInfo) {
      if (location.state?.fromStore) {
        navigate(`/${location.state.fromStore}`);
      } else {
        navigate('/');
      }
    }
    document.documentElement.classList.add('login-page');

    // Remove the class when the component unmounts
    return () => {
      document.documentElement.classList.remove('login-page');
    };
  }, [successMessage, errorMessage, userInfo, dispatch, navigate]);

  return (
    <>
    <div className="p-4 flex flex-col items-start max-w-sm mx-auto h-dvh overflow-hidden pt-20 mb-20">
      {loader && (
        <div className="w-screen h-screen flex justify-center items-center fixed left-0 top-0 bg-background/80 z-50">
          <LoaderIcon className="w-5 h-5 animate-spin" />
        </div>
      )}
     
      <div className="flex items-center w-full py-8 border-b border-border/80">
        <Link to={location.state?.fromStore ? `/${location.state.fromStore}` : "/"} className="flex items-center gap-x-2">
          <h1 className="text-lg font-medium">Stores for ecommerce</h1>
        </Link>
      </div>

      <div className="flex flex-col items-start gap-y-6 py-8 w-full px-0.5">
        <div className="w-full">
          <h2 className="text-2xl font-semibold">
            Sign in with MyPursePay
          </h2>
          <p className="text-sm text-muted-foreground mt-2">
            Customer portal - 
            <a href={`${process.env.REACT_APP_SELLER_DASHBOARD_URL}/login`} className="text-primary hover:underline ml-1">
              Merchant? Sign in here
            </a>
          </p>
        </div>
        <form onSubmit={login} className="w-full">
          <div className="space-y-2 w-full">
            <Label htmlFor="email">
              Email
            </Label>
            <Input
              id="email"
              name="email"
              type="email"
              value={state.email}
              disabled={loader}
              onChange={inputHandle}
              placeholder="Enter your email"
              className="w-full focus-visible:border-foreground"
            />
          </div>

          <div className="mt-4 space-y-2">
            <Label htmlFor="password">
              Password
            </Label>
            <div className="relative w-full">
              <Input
                id="password"
                name="password"
                type={showPassword ? "text" : "password"}
                value={state.password}
                disabled={loader}
                onChange={inputHandle}
                placeholder="Enter your password"
                className="w-full focus-visible:border-foreground"
              />
              <Button
                type="button"
                size="icon"
                variant="ghost"
                disabled={loader}
                className="absolute top-1 right-1 rounded-md"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? 
                  <EyeOff className="w-4 h-4" /> : 
                  <Eye className="w-4 h-4" />
                }
              </Button>
            </div>
          </div>

          <div className="mt-4 w-full space-y-3">
            <Button
              type="submit"
              disabled={loader}
              className="w-full"
            >
              {loader ? (
                <LoaderIcon className="w-5 h-5 animate-spin" />
              ) : "Continue"}
            </Button>

            <a 
              href="https://app.mypursepay.com/atlas" 
              target="_blank" 
              rel="noopener noreferrer"
              className="block w-full"
            >
              <Button
                type="button"
                variant="outline"
                className="w-full bg-accent text-accent-foreground"
              >
                Create account
              </Button>
            </a>
          </div>
        </form>
      </div>

      <div className="flex flex-col items-start w-full mt-4">
        <p className="text-sm text-muted-foreground">
          Need to reset your password? Open the MyPursePay app or dashboard to manage it there, then return to sign in with the updated credentials.
        </p>
      </div>

      <div className="flex items-start mt-auto border-t border-border/80 py-6 w-full">
        <p className="text-sm text-muted-foreground">
          Don&apos;t have an account? Create one through MyPursePay and use those credentials here.
        </p>
      </div>
    </div>
    <Footer />
    </>
  );
};

export default Login;